import { Link } from 'react-router-dom';
import { BookOpen, Check, GraduationCap, Lock, Mic } from 'lucide-react';
import { useMyCurricula, useSchooling, type Curriculum } from '@core/firebase/curricula';
import { useCoverage } from '@core/firebase/tracks';
import { curriculumMap, currentStage, type MapStage } from '@core/gamification/curriculumMap';
import { curriculumProgress, firstUncoveredAyah, unitAyahs } from '@core/quran/curriculumProgress';
import type { CurriculumUnit } from '@core/quran/curriculumProgress';
import { bundledQuran } from '@core/quran/source';
import { surahMeta } from '@core/quran/surahMeta';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ProgressBar } from '@/components/ProgressBar';
import { LearningEmpty, LoadingBlock, PageHeader, PILL_LIME, PILL_PRIMARY } from '@/components/learning/ui';
import { useLearnText } from '@/lib/learnText';
import { cn } from '@/lib/cn';

/**
 * What this student's grade must recite this year.
 *
 * The curriculum is not chosen and not joined: it is whatever is published for
 * their country and grade, so this page shows nothing to choose from. The one
 * thing it can be missing is the pair that selects it, and then it says so and
 * points at the profile rather than showing an empty syllabus.
 *
 * Redesign 2026-10 (board l2), at desktop width: the unit to recite next is a
 * hero card beside the term's summary, and every unit sits in a grid in the
 * school's own order — done (lime check), current (teal mic, lime ring),
 * still ahead (lock) — each with its state in words.
 */
export default function CurriculumPage() {
  const { identity } = useAuth();
  const { t, language } = useLanguage();
  const tl = useLearnText();
  const schooling = useSchooling(identity?.userId ?? null);
  const { curricula, loading } = useMyCurricula(schooling.country, schooling.grade);
  const coverage = useCoverage(identity?.userId ?? null);

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };
  const unitLabel = (unit: CurriculumUnit) => (
    unit.from === undefined
      ? t('curriculum.wholeSurah')
      : t('curriculum.ayahRange', { from: count(unit.from), to: count(unit.to as number) })
  );

  if (schooling.loading || loading) {
    return <LoadingBlock testId="curriculum-loading" />;
  }

  // The pair is what selects a syllabus. Saying "no syllabus" to someone who
  // has simply never told us their grade sends them looking for a fault that
  // is not there.
  if (!schooling.country || !schooling.grade) {
    return (
      <LearningEmpty
        testId="no-school"
        icon={GraduationCap}
        title={t('curriculum.noSchool')}
        action={(
          <Link to="/profile" data-testid="set-school" className={PILL_PRIMARY}>
            {t('curriculum.setSchool')}
          </Link>
        )}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        eyebrow={tl('learn.curriculum.grade', { n: count(schooling.grade) })}
        title={t('curriculum.title')}
        subtitle={t('curriculum.subtitle')}
      />

      {curricula.length === 0 ? (
        <LearningEmpty
          testId="no-curriculum"
          icon={BookOpen}
          title={t('curriculum.none')}
          body={t('curriculum.noneBody')}
        />
      ) : (
        curricula.map((c: Curriculum) => {
          const progress = curriculumProgress(coverage, c.units);
          const stages = curriculumMap({ coverage, units: c.units });
          const here = currentStage(stages);
          return (
            <section key={c.id} data-testid={`curriculum-${c.id}`} className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                {/* The term at a glance — first in reading order. */}
                <div className="card-soft flex flex-col justify-between gap-4 p-6">
                  <div>
                    <h2 className="text-lg font-semibold text-ink">{c.name}</h2>
                    <p className="text-sm text-helper">{t('curriculum.term', { term: count(c.term) })}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-4xl font-semibold text-ink" dir="ltr">
                      {count(progress.percent)}%
                    </p>
                    <ProgressBar percent={progress.percent} label={c.name} />
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-helper">
                      <span>
                        {t('curriculum.unitsDone', {
                          done: count(progress.done),
                          count: progress.units, n: count(progress.units),
                        })}
                      </span>
                      <span data-testid={`percent-${c.id}`}>
                        <span dir="ltr">{count(progress.covered)} / {count(progress.total)} · {count(progress.percent)}%</span>
                      </span>
                    </div>
                  </div>
                </div>

                {here ? (
                  <CurrentUnit
                    stage={here}
                    title={surahName(here.unit.surah)}
                    label={unitLabel(here.unit)}
                    resumeAt={firstUncoveredAyah(coverage, here.unit)}
                    count={count}
                    tl={tl}
                  />
                ) : (
                  <div className="card-soft flex items-center gap-4 p-6">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-lime text-accent-foreground">
                      <Check className="h-6 w-6" aria-hidden />
                    </span>
                    <p className="text-lg font-semibold text-ink">
                      {t('curriculum.unitsDone', {
                        done: count(progress.done),
                        count: progress.units, n: count(progress.units),
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/*
                * In the curriculum's own order, never sorted here: the order
                * IS the teaching plan, and the page must not present a term
                * differently from the way it was set.
                */}
              <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {stages.map((stage, i) => (
                  <UnitCard
                    key={`${stage.unit.surah}-${i}`}
                    testId={`unit-${stage.unit.surah}-${i}`}
                    stage={stage}
                    isCurrent={here?.number === stage.number}
                    title={surahName(stage.unit.surah)}
                    label={unitLabel(stage.unit)}
                    count={count}
                    t={t}
                    tl={tl}
                  />
                ))}
              </ol>
            </section>
          );
        })
      )}
    </div>
  );
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

function CurrentUnit({
  stage, title, label, resumeAt, count, tl,
}: {
  stage: MapStage;
  title: string;
  label: string;
  resumeAt: number;
  count: (n: number) => string;
  tl: Translate;
}) {
  const opening = bundledQuran.words(stage.unit.surah, resumeAt);
  const preview = opening.slice(0, 5).join(' ');
  return (
    <div data-testid="current-unit" className="hero-card flex flex-col gap-4 p-7">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full bg-lime px-3 py-1 text-xs font-semibold text-accent-foreground">
          {tl('game.map.current')}
        </span>
        <span className="text-sm opacity-80">
          {label} · {tl('learn.home.ayahOf', { n: count(resumeAt), total: count(unitAyahs(stage.unit)) })}
        </span>
      </div>
      <h3 className="text-2xl font-semibold">{title}</h3>
      {preview ? (
        <p className="ayah truncate text-3xl" lang="ar">
          {preview}{opening.length > 5 ? ' …' : ''}
        </p>
      ) : null}
      <div>
        <Link
          to={`/practice?surah=${stage.unit.surah}&ayah=${resumeAt}`}
          data-testid="continue-unit"
          className={cn(PILL_LIME, 'h-12 px-7 text-base')}
        >
          <Mic className="h-4 w-4" aria-hidden />
          {tl('learn.curriculum.continueFrom', { n: count(resumeAt) })}
        </Link>
      </div>
    </div>
  );
}

function UnitCard({
  testId, stage, isCurrent, title, label, count, t, tl,
}: {
  testId: string;
  stage: MapStage;
  isCurrent: boolean;
  title: string;
  label: string;
  count: (n: number) => string;
  t: Translate;
  tl: Translate;
}) {
  const done = stage.state === 'done';
  const Icon = done ? Check : isCurrent ? Mic : Lock;
  return (
    <li
      data-testid={testId}
      data-state={stage.state}
      className={cn('card-soft space-y-3 p-4', isCurrent && 'bg-tint')}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            done && 'bg-lime text-accent-foreground',
            isCurrent && 'bg-primary text-primary-foreground ring-2 ring-lime ring-offset-2 ring-offset-tint',
            !done && !isCurrent && 'bg-surface text-helper',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className={cn('truncate font-semibold', done || isCurrent ? 'text-ink' : 'text-helper')}>{title}</p>
          <p className="text-xs text-helper">{label}</p>
        </div>
        <span className="shrink-0 text-xs text-helper">
          {done ? tl('learn.curriculum.done') : isCurrent ? t('game.map.open') : t('tracks.ayahCount', {
            count: unitAyahs(stage.unit), n: count(unitAyahs(stage.unit)),
          })}
        </span>
      </div>
      <ProgressBar percent={stage.percent} label={title} />
      <p className="text-xs text-helper">
        <span dir="ltr">{count(stage.covered)} / {count(unitAyahs(stage.unit))}</span>
      </p>
    </li>
  );
}
